// Derives each tunnel's per-side window counts straight from the plant counts,
// instead of the block-level access_window. GreenAmex standard: 10 plants per
// window (soil) / 21.6 (hydroponic).
//
// The rounding differs by type because the two per-window values mean different
// things:
//  - Soil (10/window is exact): any leftover plants are a real, physical extra
//    window → CEIL. So 132 soil plants render 14 windows, not 13.
//  - Hydroponic (21.6/window is a fractional AVERAGE): the leftover is rounding
//    noise from the decimal, not a real window → ROUND to the nearest. ceil there
//    over-counts by one (e.g. 246/21.6 = 11.39 → 11 windows, not 12).
//
// The SUB_A/SUB_B label comes from the row_id (already correct after the
// block_rows swaps), so the map reflects the data with no inversion guessing.

export const PLANTS_PER_WINDOW = { soil: 10, hydro: 21.6 }

// Plants -> window count for a SINGLE line, the shared rule used by both the
// farming-block map and the tunnel detail view. Soil (10/window is exact) CEILs
// any leftover into a real extra window; hydroponic (21.6 is a fractional
// average) ROUNDs so the decimal noise doesn't add a phantom window. Returns 0
// for empty/invalid counts.
export const plantsToWindows = (plants, isHydroponic) => {
  const ppw = isHydroponic ? PLANTS_PER_WINDOW.hydro : PLANTS_PER_WINDOW.soil
  const n = Number(plants)
  if (!Number.isFinite(n) || n <= 0) return 0
  const windows = isHydroponic ? Math.round(n / ppw) : Math.ceil(n / ppw - 1e-9)
  return windows > 0 ? windows : 0
}

// row_id looks like `{block}[-SUB_A|-SUB_B]-T{tunnel}-R{line}` (the sub-block
// token is optional and some ids carry stray spaces). Anchor the tunnel/line at
// the end and match the SUB token as a plain substring.
export const parseTunnelSide = (rowId) => {
  const id = rowId || ''
  const m = /T(\d+)-R\d+\s*$/.exec(id)
  const tunnel = m ? Number(m[1]) : null
  const side = id.includes('SUB_A') ? 'A' : id.includes('SUB_B') ? 'B' : 'FULL'
  return { tunnel, side }
}

// Per-tunnel completion ratio (0..1) for activity progress: fraction of a
// tunnel's lines (block_rows) whose row_id is in `doneRowIds`. SUB_A and SUB_B
// count as separate lines. Used to tint each tunnel column in the overview.
export const tunnelDonePercent = (rows, doneRowIds) => {
  const total = new Map()
  const done = new Map()
  for (const row of rows || []) {
    const { tunnel } = parseTunnelSide(row?.rowId)
    if (tunnel == null) continue
    total.set(tunnel, (total.get(tunnel) || 0) + 1)
    if (doneRowIds && doneRowIds.has(row.rowId)) {
      done.set(tunnel, (done.get(tunnel) || 0) + 1)
    }
  }
  const pct = new Map()
  for (const [tunnel, count] of total) {
    pct.set(tunnel, count > 0 ? (done.get(tunnel) || 0) / count : 0)
  }
  return pct
}

// Per-(tunnel, side) completion ratio (0..1): fraction of a tunnel's SUB_A /
// SUB_B / FULL lines whose row_id is in `doneRowIds`. Lets the overview tint each
// side independently (a tunnel can have SUB_B done and SUB_A pending). Keyed by
// `${tunnel}|${side}` where side is 'A' | 'B' | 'FULL'.
export const tunnelSideDonePercent = (rows, doneRowIds) => {
  const total = new Map()
  const done = new Map()
  for (const row of rows || []) {
    const { tunnel, side } = parseTunnelSide(row?.rowId)
    if (tunnel == null) continue
    const key = `${tunnel}|${side}`
    total.set(key, (total.get(key) || 0) + 1)
    if (doneRowIds && doneRowIds.has(row.rowId)) {
      done.set(key, (done.get(key) || 0) + 1)
    }
  }
  const pct = new Map()
  for (const [key, count] of total) {
    pct.set(key, count > 0 ? (done.get(key) || 0) / count : 0)
  }
  return pct
}

// Build a per-tunnel window map from the block rows.
// Returns { byTunnel: Map<tunnelNumber, { subA, subB, full }>, maxSubA, maxSubB, hasData }.
export const deriveTunnelWindows = (rows, isHydroponic) => {
  // tunnel -> { A: [windows...], B: [...], FULL: [...] }
  const buckets = new Map()

  for (const row of rows || []) {
    const { tunnel, side } = parseTunnelSide(row?.rowId)
    if (tunnel == null) continue
    const windows = plantsToWindows(row?.numberOfPlants, isHydroponic)
    if (windows <= 0) continue
    if (!buckets.has(tunnel)) buckets.set(tunnel, { A: [], B: [], FULL: [] })
    buckets.get(tunnel)[side].push(windows)
  }

  // A tunnel's window count per side is the DEEPEST line (max), not the most
  // common one: if a single line is long enough to reach one more window, that
  // window is real and must show (per user requirement). Using the max also means
  // editing one line's plants immediately changes the rendered depth.
  const maxOr0 = (arr) => (arr.length ? Math.max(...arr) : 0)
  const byTunnel = new Map()
  let maxSubA = 0
  let maxSubB = 0
  for (const [tunnel, sides] of buckets) {
    const subA = maxOr0(sides.A)
    const subB = maxOr0(sides.B)
    const full = maxOr0(sides.FULL)
    byTunnel.set(tunnel, { subA, subB, full })
    if (subA > maxSubA) maxSubA = subA
    if (subB > maxSubB) maxSubB = subB
  }

  return { byTunnel, maxSubA, maxSubB, hasData: byTunnel.size > 0 }
}
